import MapView from "@arcgis/core/views/MapView";
import Basemap from "@arcgis/core/Basemap";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { LayerType } from "../../types/layer-type";
import {
  clearSelectedGraphic,
  extentEstateEn,
  focusOnFeature,
  getLayers,
  initializeMap,
  polylineOff,
  polylineOn,
  setConvertedFeatures,
  takeScreenshotAsyncAction,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  MapSection,
  masterPlanDataTypes,
  setAttributesModalSelectedFeature,
  setEnableDrawingTools,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  startDrawingTools,
  stopDrawingTools,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  applyHoverEffect,
  changeBaseMap,
  CreateHybridBaseMap,
  finishDrawingProcess,
  getPopUpTemplate,
  handleExtentEstate,
  handleFocusOnFeature,
  resetHoverEffects,
  startDrawingProcess,
  stopMeasuring,
  unFocus,
  updateSnappingSources,
  waitForViewerReady,
} from "../../utils/helpers-for-2d-map";
import { LayersDataTypes } from "../../types/layers-data-types";
import { Middleware } from "redux";
import { RootState } from "../store";
import { ArcgisHybridLayer, Layer } from "../../interfaces/layer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import {
  getGeometryForContour,
  setEditingBorders,
  setLayerVisibilityByEstateEn,
} from "../actions/master-plan-modification";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import {
  FormationTypeNames,
  setGeometries,
  setScreenshot,
} from "../reducers/master-plan-modification";
import Polygon from "@arcgis/core/geometry/Polygon";
import { v4 as uuidv4 } from "uuid";
import {
  ConvertPolygonsToCoordinatesTable,
  ConvertToPolygons,
  isPolygon,
} from "../../utils/master-plan-helper";
import {
  addConvertedFeaturesToLayer,
  applyEditToPlotGeometry,
  buildDefinitionExpression,
  buildVertexIndexMap,
  clearLastSelectedGraphic,
  DrawVertices,
  DrawVerticesLabels,
  EditGraphics,
  EndCreateGraphic,
  getGeometry,
  handleDeleteKey,
  handleFeatureClickStyle,
  handleViewerClick,
} from "../../utils/master-map-helper";
import Polyline from "@arcgis/core/geometry/Polyline";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import PrintParameters from "@arcgis/core/rest/support/PrintParameters";
import * as print from "@arcgis/core/rest/print";
import { FieldInfo } from "../../interfaces/krookie";
import Color from "@arcgis/core/Color";
import { ensureIdentityTokenForResource } from "../../utils/arcgis-auth";

export const masterPlanMiddleware = (
  section: MapSection,
): Middleware<{}, RootState> => {
  return (store) => {
    let viewer: MapView | null = null;
    let baseMap: Basemap;
    let tempLayer: GraphicsLayer;
    let linesLayer: GraphicsLayer;
    let verticesTempLayer: GraphicsLayer;
    let verticesLabelTempLayer: GraphicsLayer;
    let loadedObjectsLayer: GraphicsLayer;
    let polygonsLayer: GraphicsLayer;
    let staticGeometryLayer: GraphicsLayer;
    let sketchViewModel: SketchViewModel;
    const { dispatch } = store;

    const polygonTempSymbol = new SimpleFillSymbol({
      color: "#2E1A4759",
      outline: { color: "#2E1A47CC", width: 3 },
    });

    function AddLayers(viewer: MapView) {
      const { layers, visibleLayersId } = store.getState().map;

      const baseMapLayer = layers.find(
        (l) =>
          l.dataType === LayersDataTypes.BaseMap &&
          visibleLayersId.includes(l.id),
      );
      if (baseMapLayer && baseMapLayer !== ArcgisHybridLayer) {
        changeBaseMap(baseMapLayer, viewer, getBaseMap);
      }
      const allVisible = [
        ...visibleLayersId,
        ...layers
          .filter(
            (item) =>
              !item.parentId &&
              !item.hidden &&
              !visibleLayersId.includes(item.id) &&
              validArcgisLayerTypes.includes(item.type) &&
              (item.dataType === LayersDataTypes.Plots ||
                item.dataType === LayersDataTypes.InvestmentOpportunities),
          )
          .map((item) => item.id),
      ];
      dispatch(setLayersVisible(allVisible));
      layers
        .filter(
          (layer) =>
            masterPlanDataTypes.includes(layer.dataType) &&
            layer.type === LayerType.ArcgisFeatureServer,
        )
        .forEach((layer) => {
          if (layer.dataType === LayersDataTypes.BaseMap) return;
          const visible =
            visibleLayersId.includes(layer.id) ||
            (!!layer.parentId && visibleLayersId.includes(layer.parentId));
          const layerProperties = {
            title: layer.label,
            id: layer.name,
            url: layer.url,
            visible: visible,
            editingEnabled: layer.canEdit ?? false,
            //TODO temporary crutch
            definitionExpression: layer.style2D?.query?.replace(
              " and LandUse <> 3",
              "",
            ),
            outFields: ["*"],
            ...(layer.style2D?.label?.type === "onclick" &&
              layer.style2D?.label && {
                popupTemplate: new PopupTemplate(
                  getPopUpTemplate(layer.style2D?.label),
                ),
              }),
          };
          if (layer.style2D?.default) {
            layerProperties.renderer = layer.style2D?.default;
          }
          const arcgisLayer = new FeatureLayer(layerProperties);

          let hoverStyle = layer.style2D?.hover;
          const isHover = layer.style2D?.label?.type === "hover";
          const mode = (hoverStyle ? "style" : "") + (isHover ? "popup" : "");

          if (mode) {
            if (isHover && layer.style2D?.label) {
              arcgisLayer.popupTemplate = new PopupTemplate(
                getPopUpTemplate(layer.style2D?.label),
              );
            }
            applyHoverEffect(
              viewer,
              arcgisLayer,
              mode,
              hoverStyle,
              layer.style2D?.fieldName,
            );
          }

          viewer.map.add(arcgisLayer);
        });
    }
    function getBaseMap(layer: Layer) {
      return layer === ArcgisHybridLayer ? baseMap : null;
    }
    function DrawTempGeometry(enableClearSelectedGraphic: boolean = false) {
      if (sketchViewModel.state === "active") {
        sketchViewModel.complete();
      }

      const polygonsAndLinesIds = [
        ...polygonsLayer.graphics,
        ...linesLayer.graphics,
      ].map((g) => g.attributes.uuid);

      verticesTempLayer.removeMany(
        verticesTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      verticesLabelTempLayer.removeMany(
        verticesLabelTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      polygonsLayer.removeAll();
      linesLayer.removeAll();

      let lastGraphic: Graphic | null = null;

      store
        .getState()
        .masterPlanModification.krookiePlots.filter((plot) => plot.newGeometry)
        .forEach((plot) => {
          if (!plot.newGeometry) return;

          const spatialReference = plot.newGeometry.spatialReference;
          const allRings = plot.newGeometry.rings || [];

          let coordIndexMap: Map<string, number> = new Map();

          const closedRingsWithIndex: { ring: number[][]; index: number }[] =
            [];
          const openPaths: { path: number[][]; index: number }[] = [];

          coordIndexMap = buildVertexIndexMap(allRings);

          const uuid = uuidv4();

          allRings.forEach((ring, index) => {
            if (isPolygon(ring)) {
              closedRingsWithIndex.push({ ring, index });
            } else {
              openPaths.push({ path: ring, index });
            }
          });

          if (closedRingsWithIndex.length) {
            const closedRings = closedRingsWithIndex.map((i) => i.ring);
            const closedRingIndices = closedRingsWithIndex.map((i) => i.index);

            const polygon = new Polygon({
              rings: closedRings,
              spatialReference,
            });

            const polygonGraphic = new Graphic({
              geometry: polygon,
              symbol: polygonTempSymbol,
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndices: closedRingIndices,
              },
            });

            polygonsLayer.add(polygonGraphic);
            lastGraphic = polygonGraphic;

            DrawVertices(polygon, uuid, false, verticesTempLayer);
            DrawVerticesLabels(
              polygon,
              uuid,
              false,
              verticesLabelTempLayer,
              coordIndexMap,
            );
          }

          openPaths.forEach(({ path, index }) => {
            const polyline = new Polyline({ paths: [path], spatialReference });

            const lineGraphic = new Graphic({
              geometry: polyline,
              symbol: new SimpleLineSymbol({ color: "#2E1A47CC", width: 2 }),
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndex: index,
              },
            });

            linesLayer.add(lineGraphic);
            lastGraphic = lineGraphic;

            DrawVertices(polyline, uuid, false, verticesTempLayer);
            DrawVerticesLabels(
              polyline,
              uuid,
              false,
              verticesLabelTempLayer,
              coordIndexMap,
            );
          });
        });

      if (lastGraphic && !enableClearSelectedGraphic) {
        const graphic = lastGraphic as Graphic;
        const targetLayer =
          graphic.geometry.type === "polyline" ? linesLayer : polygonsLayer;
        handleFeatureClickStyle(
          graphic,
          store,
          targetLayer,
          viewer,
          sketchViewModel,
        );
      } else {
        clearSelection();
      }
    }

    function DrawStaticGeometry() {
      staticGeometryLayer.removeAll();

      const selectedPlot =
        store.getState().masterPlanModification.selectedPlots[0];

      if (!selectedPlot.geometry) return;

      const plotGeometry = selectedPlot.geometry;

      const staticGraphic = new Graphic({
        geometry: plotGeometry,
        symbol: new SimpleFillSymbol({
          color: "#FAFAFA33",
          outline: {
            color: "#FFFFFF",
            width: 3,
          },
        }),
      });

      staticGeometryLayer.add(staticGraphic);
    }

    function updateSnappingWrapper() {
      const ignoredLayers = [verticesTempLayer, verticesLabelTempLayer];

      updateSnappingSources(viewer, sketchViewModel, ignoredLayers);
    }

    function clearSelection() {
      clearLastSelectedGraphic([
        loadedObjectsLayer,
        linesLayer,
        tempLayer,
        polygonsLayer,
      ]);
      dispatch(setAttributesModalSelectedFeature(null));
    }

    function hideOtherPlotVertices(hide: boolean, currentObjectId?: number) {
      if (hide && currentObjectId) {
        const currentPlotGraphics = polygonsLayer.graphics
          .toArray()
          .filter((g) => g.attributes.objectId === currentObjectId);

        const currentUuids = currentPlotGraphics.map((g) => g.attributes.uuid);

        verticesTempLayer.graphics.forEach((graphic) => {
          if (!currentUuids.includes(graphic.attributes.uuid)) {
            graphic.visible = false;
          }
        });

        verticesLabelTempLayer.graphics.forEach((graphic) => {
          if (!currentUuids.includes(graphic.attributes.uuid)) {
            graphic.visible = false;
          }
        });
      } else {
        verticesTempLayer.graphics.forEach((graphic) => {
          graphic.visible = true;
        });

        verticesLabelTempLayer.graphics.forEach((graphic) => {
          graphic.visible = true;
        });
      }
    }

    function highlightObjectForScreenshot(currentObjectId: number) {
      polygonsLayer.graphics.forEach((graphic) => {
        const isCurrentObject = graphic.attributes.objectId === currentObjectId;
        const polygonScreenshotSymbol = polygonTempSymbol.clone();
        polygonScreenshotSymbol.outline.color = new Color("#000000");
        polygonScreenshotSymbol.outline.width = 1;

        if (!isCurrentObject) {
          graphic.symbol = polygonScreenshotSymbol;
        }
      });
    }

    function resetPolygonsToDefaultStyle() {
      polygonsLayer.graphics.forEach((graphic) => {
        graphic.symbol = polygonTempSymbol;
      });
    }

    function moveGraphicsLayersToTop() {
      const graphicsLayers = [
        loadedObjectsLayer,
        staticGeometryLayer,
        polygonsLayer,
        linesLayer,
        tempLayer,
        verticesTempLayer,
        verticesLabelTempLayer,
      ];

      graphicsLayers.forEach((layer) => {
        if (viewer && viewer.map.layers.includes(layer)) {
          viewer.map.reorder(layer, viewer.map.layers.length);
        }
      });
    }

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (action.payload.section === section) {
          if (viewer === null) {
            await next(action);

            baseMap = CreateHybridBaseMap();
            viewer = createViewer(
              store,
              AddLayers,
              `${section}MapContainer`,
              baseMap,
            );

            tempLayer = new GraphicsLayer();
            verticesTempLayer = new GraphicsLayer();
            verticesLabelTempLayer = new GraphicsLayer();
            linesLayer = new GraphicsLayer();
            loadedObjectsLayer = new GraphicsLayer();
            polygonsLayer = new GraphicsLayer();
            staticGeometryLayer = new GraphicsLayer();

            viewer.map.add(loadedObjectsLayer);
            viewer.map.add(staticGeometryLayer);
            viewer.map.add(polygonsLayer);
            viewer.map.add(linesLayer);
            viewer.map.add(tempLayer);
            viewer.map.add(verticesTempLayer);
            viewer.map.add(verticesLabelTempLayer);

            sketchViewModel = new SketchViewModel({
              updateOnGraphicClick: false,
              defaultUpdateOptions: {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                  shapeOperation: "none",
                },
              },
              layer: tempLayer,
              view: viewer,
              snappingOptions: {
                enabled: true,
                selfEnabled: false,
                featureSources: [],
              },
              polygonSymbol: new SimpleFillSymbol({
                color: "#FAFAFA80",
                outline: {
                  color: "#E59E6D",
                  width: 3,
                },
              }),
            });

            sketchViewModel.on("update", (event) => {
              if (event.toolEventInfo) {
                (async () => {
                  const mergedGeometryGraphic = await applyEditToPlotGeometry(
                    event.graphics[0],
                    store.getState().masterPlanModification.krookiePlots,
                  );
                  if (mergedGeometryGraphic) {
                    const rings = (mergedGeometryGraphic!.geometry as Polygon)
                      .rings;
                    const coordIndexMap = buildVertexIndexMap(rings);
                    EditGraphics(
                      [mergedGeometryGraphic],
                      dispatch,
                      verticesLabelTempLayer,
                      undefined,
                      coordIndexMap,
                    );
                  }
                })();
              } else {
                if (event.state === "start" || event.state === "complete") {
                  event.graphics.forEach((graphic) => {
                    DrawVertices(
                      graphic.geometry as Polygon,
                      graphic.attributes.uuid,
                      event.state === "start",
                      verticesTempLayer,
                    );
                  });
                }
              }
            });
            sketchViewModel.on("create", (event) => {
              if (event.state === "cancel" && !event.graphic?.attributes) {
                return;
              }
              if (event.state === "start") {
                event.graphic.attributes = { uuid: uuidv4() };
              }
              if (event.state === "complete") {
                DrawVertices(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  false,
                  verticesTempLayer,
                );

                handleFeatureClickStyle(
                  event.graphic,
                  store,
                  tempLayer,
                  viewer,
                  sketchViewModel,
                );
              }
              if (event.graphic.attributes.objectId) {
                DrawVerticesLabels(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  event.state === "cancel",
                  verticesLabelTempLayer,
                );
              }
            });

            viewer!.on("click", async (event) => {
              const response = await viewer!.hitTest(event);

              const clickedGraphicResult = response.results[0];

              handleViewerClick(
                event,
                store,
                sketchViewModel,
                viewer,
                [tempLayer, linesLayer, loadedObjectsLayer, polygonsLayer],
                tempLayer,
                loadedObjectsLayer,
              );

              if (clickedGraphicResult && "graphic" in clickedGraphicResult) {
                const graphic = clickedGraphicResult.graphic;

                const isLoadedObjectsLayer =
                  graphic.layer === loadedObjectsLayer;
                const isFeatureLayer = graphic.layer instanceof FeatureLayer;

                let fieldsInfo: FieldInfo[] | null = null;
                let layerUrl: string | null = null;

                if (isLoadedObjectsLayer || isFeatureLayer) {
                  if (isFeatureLayer) {
                    const layerId = graphic.layer.id;
                    const layers = store
                      .getState()
                      .map.layers.filter((l) => l.name === layerId);
                    const isAllowed = layers.some(
                      (l) =>
                        l.dataType === LayersDataTypes.Plots ||
                        l.dataType === LayersDataTypes.InvestmentOpportunities,
                    );
                    const featureLayer = graphic.layer as FeatureLayer;
                    fieldsInfo = featureLayer.fields.map((field) => {
                      let codedValueDict: Record<string, string> | null = null;
                      if (field.domain && field.domain.type === "coded-value") {
                        codedValueDict = {};
                        field.domain.codedValues.forEach((cv) => {
                          codedValueDict![cv.code] = cv.name;
                        });
                      }
                      return {
                        fieldName: field.name,
                        type: field.type,
                        editable: field.editable,
                        codedValueDict: codedValueDict,
                        nullable: field.nullable,
                        defaultValue: field.defaultValue,
                        maxLength: field.length,
                      };
                    });
                    layerUrl = `${featureLayer.url}/${featureLayer.layerId}`;

                    if (!isAllowed) return;
                  }

                  dispatch(
                    setAttributesModalSelectedFeature({
                      layerName:
                        graphic.layer instanceof FeatureLayer
                          ? graphic.layer.title
                          : (graphic as any).layerName,
                      attributes: graphic.attributes,
                      fieldsInfo,
                      layerUrl,
                    }),
                  );
                  return;
                }
              } else {
                clearSelection();
              }
            });

            await waitForViewerReady(viewer);

            viewer.container.addEventListener("keydown", (event) => {
              const krookePlots =
                store.getState().masterPlanModification.krookiePlots;

              if (event.key === "Escape") {
                clearSelection();
                return;
              }

              handleDeleteKey(
                event,
                store,
                [tempLayer, linesLayer, polygonsLayer],
                verticesTempLayer,
                krookePlots,
                () =>
                  store.dispatch(
                    setGeometries({
                      newGeometry: ConvertToPolygons([]),
                      stringGeometry: ConvertPolygonsToCoordinatesTable([]),
                    }),
                  ),
                verticesLabelTempLayer,
                false,
                sketchViewModel,
              );
            });
            dispatch(setMapComponentReady(section));
            updateSnappingWrapper();
            moveGraphicsLayersToTop();
          }
          return;
        } else if (viewer !== null) {
          unFocus();
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }
      if (setEditingBorders.match(action)) {
        const mode = action.payload;
        const shouldReset = mode === "reset" || mode === "resetAndDraw";
        const shouldDraw = mode === "draw" || mode === "resetAndDraw";

        const plots = store.getState().masterPlanModification.selectedPlots;
        const krookieFormationTypeName =
          store.getState().masterPlanModification.selectedFormationType;
        const selectedCity =
          store.getState().masterPlanModification.selectedCity;

        const processedLayers = new Set();

        for (const plot of plots) {
          const hasPlotLayer =
            plot && plot.layer && Object.keys(plot.layer).length > 0;

          if (hasPlotLayer && !processedLayers.has(plot.layer.name)) {
            processedLayers.add(plot.layer.name);

            const layer = viewer?.map.findLayerById(
              plot.layer.name,
            ) as FeatureLayer;

            if (!layer) {
              continue;
            }

            if (!layer.loaded) {
              try {
                await layer.load();
              } catch (error) {
                throw error;
              }
            }

            const objectIdField = layer.objectIdField;
            const objectIds = plots
              .filter((p) => p.layer.name === plot.layer.name)
              .map((p) => p.objectId);

            const filteredQuery = (plot.layer.style2D?.query || "1=1").replace(
              " and LandUse <> 3",
              "",
            );

            if (shouldReset) {
              layer.definitionExpression = filteredQuery;
            }

            if (shouldDraw && selectedCity) {
              layer.definitionExpression = buildDefinitionExpression({
                estateIds: [selectedCity],
                objectIds,
                objectIdField,
                baseQuery: filteredQuery,
              });
            }
          }
        }

        if (shouldReset) {
          tempLayer.removeAll();
          polygonsLayer.removeAll();
          linesLayer.removeAll();
          verticesTempLayer.removeAll();
          verticesLabelTempLayer.removeAll();
          staticGeometryLayer.removeAll();
        }

        if (shouldDraw) {
          unFocus();
          dispatch(setEnableDrawingTools(true));

          if (krookieFormationTypeName === FormationTypeNames.Divide) {
            DrawStaticGeometry();
          }
          DrawTempGeometry();
        }
      }

      if (setGeometries.match(action)) {
        next(action);
        DrawTempGeometry();
        return;
      }

      if (startDrawingTools.match(action)) {
        tempLayer.graphics.forEach((graphic) => {
          DrawVertices(
            graphic.geometry as Polygon,
            graphic.attributes.uuid,
            false,
            verticesTempLayer,
          );
        });
      }

      if (stopDrawingTools.match(action)) {
        if (
          sketchViewModel.state === "active" &&
          sketchViewModel.createGraphic
        ) {
          EndCreateGraphic(sketchViewModel);
        } else {
          sketchViewModel.complete();
        }
        const uuids = tempLayer.graphics.map(
          (graphic) => graphic.attributes.uuid,
        );
        verticesTempLayer.removeMany(
          verticesTempLayer.graphics
            .filter((g) => uuids.includes(g.attributes.uuid))
            .toArray(),
        );
      }

      if (takeScreenshotAsyncAction.match(action)) {
        const { data, deferred } = action.payload;

        resetHoverEffects(viewer);

        tempLayer.removeAll();
        DrawTempGeometry(true);
        staticGeometryLayer.visible = false;

        const printServiceUrl =
          "https://gis.madayn.om/server/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task";

        try {
          await ensureIdentityTokenForResource(store, printServiceUrl);

          for (let item of data) {
            const objectId = item.objectId;

            if (objectId) {
              highlightObjectForScreenshot(objectId);
              hideOtherPlotVertices(true, objectId);
            }

            await viewer.goTo({ target: item.geometry });

            if (item.zoomAdjustment) {
              await viewer.goTo({
                target: item.geometry,
                zoom: viewer.zoom + item.zoomAdjustment,
              });
            }

            const watermarkHeight = 300;
            const crop = watermarkHeight / 2;

            const template = new PrintTemplate({
              format: "jpg",
              layout: "map-only",
              scalePreserved: true,
              outScale: viewer.scale,
              exportOptions: {
                width: 1236,
                height: 1133 + watermarkHeight,
                dpi: 300,
              },
            });

            const parameters = new PrintParameters({
              view: viewer,
              template,
            });

            const { url } = await print.execute(printServiceUrl, parameters);

            const response = await fetch(url);
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);

            const img = new Image();
            img.src = imageUrl;

            await new Promise((resolve) => (img.onload = resolve));

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height - watermarkHeight;

            const ctx = canvas.getContext("2d");

            if (!ctx) return;

            ctx.drawImage(
              img,
              0,
              crop,
              img.width,
              canvas.height,
              0,
              0,
              img.width,
              canvas.height,
            );

            const croppedUrl = canvas.toDataURL("image/jpeg", 1.0);
            URL.revokeObjectURL(imageUrl);

            if (objectId) {
              dispatch(
                setScreenshot({
                  id: objectId,
                  screenshot: {
                    url: croppedUrl,
                    scale: Math.round(viewer.scale),
                  },
                }),
              );
            }

            resetPolygonsToDefaultStyle();
            hideOtherPlotVertices(false);
          }

          staticGeometryLayer.visible = true;
          deferred.resolve([]);
        } catch (err) {
          deferred.reject(err);
        }
      }

      if (focusOnFeature.match(action)) {
        let { layer, geometry, objectId } = action.payload;
        const plot = store
          .getState()
          .masterPlanModification.krookiePlots.find(
            (p) => p.objectId === objectId,
          );
        if (plot?.newGeometry) {
          geometry = plot.newGeometry;
        }
        handleFocusOnFeature(
          layer,
          geometry,
          objectId,
          dispatch,
          store,
          viewer,
          false,
          false,
        );
      }

      if (setLayersVisible.match(action)) {
        next(action);
        const { layers, visibleLayersId } = store.getState().map;
        layers
          .filter((layer) => masterPlanDataTypes.includes(layer.dataType))
          .forEach((layer) => {
            if (layer.dataType === LayersDataTypes.BaseMap) {
              if (visibleLayersId.includes(layer.id))
                changeBaseMap(layer, viewer, getBaseMap);
            } else if (validArcgisLayerTypes.includes(layer.type)) {
              let mapLayers = viewer!.map.layers.filter(
                (l) => l.id === layer.name,
              );
              if (mapLayers) {
                const visible =
                  visibleLayersId.includes(layer.id) ||
                  (!!layer.parentId &&
                    visibleLayersId.includes(layer.parentId));
                Promise.all(
                  mapLayers.map((mapLayer) => {
                    mapLayer.visible = visible;
                    return mapLayer.load();
                  }),
                ).finally(() => {
                  dispatch(setLoading({ id: layer.id, loading: false }));
                });
              }
            }
          });
        moveGraphicsLayersToTop();
        return;
      }

      if (getGeometryForContour.match(action)) {
        const { plotIdx, contourIdx } = action.payload;
        const coordinatesData =
          store.getState().masterPlanModification.coordinatesData;
        const existingGeometry =
          store.getState().masterPlanModification.krookiePlots?.[0]?.geometry;
        const targetWkid = existingGeometry?.spatialReference?.wkid;

        const result = await getGeometry(
          plotIdx,
          contourIdx,
          targetWkid,
          tempLayer,
          coordinatesData,
        );

        if (!result) return;

        const { uuid, updatedData } = result;

        [tempLayer, verticesTempLayer, verticesLabelTempLayer].forEach(
          (layer) => {
            const toRemove = layer.graphics
              .filter((g) => g.attributes?.uuid === uuid)
              .toArray();
            layer.graphics.removeMany(toRemove);
          },
        );

        dispatch(
          setGeometries({
            newGeometry: ConvertToPolygons(updatedData),
            stringGeometry: ConvertPolygonsToCoordinatesTable(updatedData),
          }),
        );
      }

      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }

      if (setLayerVisibilityByEstateEn.match(action)) {
        const { layers } = store.getState().map;

        const dataLayersToFilterByEstate = [
          LayersDataTypes.Plots,
          LayersDataTypes.InvestmentOpportunities,
          LayersDataTypes.CityBoundary,
        ];

        const { estateIds } = action.payload;

        viewer.map.layers.forEach((layer) => {
          const foundLayer = layers.find((l) => l.name === layer.id);

          if (
            foundLayer &&
            dataLayersToFilterByEstate.includes(foundLayer.dataType)
          ) {
            const featureLayer = layer as FeatureLayer;

            const filteredQuery = (foundLayer.style2D?.query || "1=1").replace(
              " and LandUse <> 3",
              "",
            );

            featureLayer.definitionExpression = buildDefinitionExpression({
              estateIds,
              baseQuery: filteredQuery,
            });
          }
        });

        return;
      }

      if (setConvertedFeatures.match(action)) {
        const convertedGeometry = store.getState().map.convertedGeometry;
        addConvertedFeaturesToLayer(convertedGeometry, loadedObjectsLayer);
        return next(action);
      }

      if (clearSelectedGraphic.match(action)) {
        clearSelection();
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        AddLayers(viewer);
        return;
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }

      if (extentEstateEn.match(action)) {
        const { estateEn, highlightFeature = false } = action.payload;
        handleExtentEstate(
          estateEn,
          store,
          viewer,
          false,
          true,
          highlightFeature,
        );
      }

      next(action);
    };
  };
};
